<template>
	<v-dialog v-model="dialog" max-width="500px" persistent>
		<v-card>
			<v-card-title class="text-h1 font-weight-bold">
				Guest Link
				<v-spacer></v-spacer>
				<v-btn icon="mdi-close" @click="close" :disabled="loading" variant="plain"> </v-btn>
			</v-card-title>
			<v-card-text>
				<v-form ref="form" v-model="valid" @submit.prevent>
					<div>
						<label class="text-caption">Generate a guest url for:</label>
						<v-text-field
							v-model="guestName"
							placeholder="Enter name"
							:rules="nameRules"
							variant="outlined"
							density="compact"
							single-line
							:disabled="loading"
						></v-text-field>
					</div>
					<div v-if="generatedUrl">
						<label class="text-caption">Link</label>
						<v-text-field
							readonly
							variant="outlined"
							density="compact"
							single-line
							:model-value="generatedUrl"
							append-inner-icon="mdi-content-copy"
							@click:append-inner="copyToClipboard"
						></v-text-field>
					</div>
				</v-form>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn variant="text" color="primary" @click="close" :disabled="loading">
					{{ generatedUrl ? "Close" : "Cancel" }}
				</v-btn>
				<v-btn
					variant="flat"
					color="primary"
					@click="generate"
					:loading="loading"
					:disabled="!valid || loading"
				>
					Generate
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import {uuid} from "vue-uuid";
import CryptoJS from "crypto-js";

export default {
	name: "GuestLinkDialog",

	props: {
		modelValue: {
			type: Boolean,
			default: false
		}
	},

	emits: ["update:modelValue"],

	data() {
		return {
			valid: false,
			loading: false,
			guestName: "",
			generatedUrl: "",
			nameRules: [
				v => !!v || "Name is required",
				v =>
					/^[a-zA-Z0-9\s\-._]+$/.test(v) ||
					"Only letters, numbers, spaces, dashes, dots and underscores are allowed"
			]
		};
	},

	computed: {
		dialog: {
			get() {
				return this.modelValue;
			},
			set(val) {
				this.$emit("update:modelValue", val);
			}
		}
	},

	watch: {
		modelValue(val) {
			if (val) {
				this.reset();
			}
		}
	},

	methods: {
		reset() {
			this.guestName = "";
			this.generatedUrl = "";
			this.loading = false;
			if (this.$refs.form) {
				this.$refs.form.resetValidation();
			}
		},

		close() {
			this.dialog = false;
		},

		async generate() {
			const {valid} = await this.$refs.form.validate();
			if (!valid) {
				return;
			}

			this.loading = true;
			const guestId = `guestp-${uuid.v4()}`;

			try {
				const token = await this.$store.dispatch("getWebRTCGuestToken", guestId);
				const station = this.$store.getters.getWebRTCPlayerKey();
				const stream = this.$store.getters.liveModeSelectedStation().streamname;
				const user = `${guestId}_${this.guestName}`;

				const data = JSON.stringify({station, stream, user, token});
				const encrypted = CryptoJS.AES.encrypt(data, this.guestName).toString();

				this.generatedUrl = `${window.location.host}/guest?data=${encodeURIComponent(encrypted)}`;
			} catch (error) {
				console.error("Failed to generate guest token:", error);
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "Failed to generate guest token. Please try again."
				});
			} finally {
				this.loading = false;
			}
		},

		copyToClipboard() {
			if (navigator.clipboard && this.generatedUrl) {
				navigator.clipboard.writeText(this.generatedUrl).then(() => {
					this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {message: "Link copied to clipboard"});
				});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
label {
	display: block;
	margin-bottom: 4px;
}
</style>
